import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import splashIcon from "../assets/images/splash-icon.png";
import { showSuccess, showError } from "../utils/toast";
import { tokenService } from "../services/tokenService";
import { verifySubscriptionApi } from "../api/billing.api";
import type { ApiError } from "../types/api";
import Button from "../components/ui/Button";
import { getTheme } from "../config/theme";

const theme = getTheme("light").colors;
const subtleBorder = "#E7EAF0";

export default function SubscriptionCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("");

  // Get params from URL
  const paymentStatus = searchParams.get("status");
  const sessionId = searchParams.get("session_id");
  const plan = searchParams.get("plan");
  const error = searchParams.get("error");
  const type = searchParams.get("type"); // "credits" for credit purchases
  const amount = searchParams.get("amount");

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    // Check if user is authenticated
    const accessToken = tokenService.getAccessToken();
    if (!accessToken) {
      setStatus("error");
      setMessage("Session expired. Please log in again.");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
      return;
    }

    // Handle error from Stripe
    if (error) {
      setStatus("error");
      setMessage(decodeURIComponent(error));
      return;
    }

    // Handle cancelled payment
    if (paymentStatus === "cancelled" || paymentStatus === "canceled") {
      setStatus("cancelled");
      setMessage("Payment was cancelled. No charges were made.");
      return;
    }

    // Handle successful payment - Stripe returns with session_id
    // Note: Stripe only sends session_id, status param is optional
    if (sessionId) {
      try {
        // Verify subscription with backend
        const response = await verifySubscriptionApi(sessionId);
        
        if (response.success) {
          setStatus("success");
          
          // Set appropriate message based on purchase type
          if (type === "credits") {
            setMessage(amount ? `Successfully purchased ${amount} credits!` : "Credits purchased successfully!");
          } else {
            setMessage(
              plan
                ? `Successfully subscribed to ${plan.replace(/_/g, " ")}!`
                : "Subscription updated successfully!"
            );
          }
          showSuccess("Payment successful!");

          // Redirect to subscription page after delay
          setTimeout(() => {
            navigate("/dashboard/subscription", { replace: true });
          }, 2000);
        } else {
          setStatus("error");
          setMessage(response.message || "Failed to verify subscription. Please contact support.");
          showError("Subscription verification failed");
        }
      } catch (err) {
        setStatus("error");
        setMessage(
          (err as ApiError)?.response?.data?.message ||
            "Failed to verify payment. Please contact support."
        );
        showError("Payment verification failed");
      }
      return;
    }

    // Unknown state
    setStatus("error");
    setMessage("Invalid callback. Please try again.");
  };

  const handleRetry = () => {
    navigate("/dashboard/subscription", { replace: true });
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard/overview", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8" style={{ background: theme.background }}>
      <div className="w-full max-w-md">
        <div className="rounded-2xl border p-8" style={{ borderColor: subtleBorder, background: theme.card }}>
          <div className="text-center">
            <img
              src={splashIcon}
              alt="ToronQ logo"
              className="mx-auto h-16 w-auto object-contain"
            />

            {/* Processing State */}
            {status === "processing" && (
              <>
                <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.infoBg }}>
                  <Loader2 size={24} className="animate-spin" style={{ color: theme.primary }} />
                </div>
                <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                  Processing Payment
                </h1>
                <p className="mt-3 text-sm" style={{ color: theme.textMuted }}>
                  Please wait while we verify your payment...
                </p>
              </>
            )}

            {/* Success State */}
            {status === "success" && (
              <>
                <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.successBg }}>
                  <Check size={24} style={{ color: theme.success }} />
                </div>
                <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                  Payment Successful!
                </h1>
                <p className="mt-3 text-sm" style={{ color: theme.textMuted }}>{message}</p>
                <p className="mt-2 text-xs" style={{ color: theme.textLight }}>
                  Redirecting to your subscription...
                </p>
              </>
            )}

            {/* Cancelled State */}
            {status === "cancelled" && (
              <>
                <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.warningBg }}>
                  <X size={24} style={{ color: theme.warning }} />
                </div>
                <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                  Payment Cancelled
                </h1>
                <p className="mt-3 text-sm" style={{ color: theme.textMuted }}>{message}</p>
                <div className="mt-8 space-y-3">
                  <Button variant="primary" theme={theme} onClick={handleRetry} className="w-full py-3">
                    Try Again
                  </Button>
                  <Button variant="secondary" theme={theme} onClick={handleGoToDashboard} className="w-full py-3">
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}

            {/* Error State */}
            {status === "error" && (
              <>
                <div className="mx-auto mt-8 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: theme.dangerBg }}>
                  <X size={24} style={{ color: theme.danger }} />
                </div>
                <h1 className="mt-6 text-2xl font-semibold tracking-tight" style={{ color: theme.text }}>
                  Something Went Wrong
                </h1>
                <p className="mt-3 text-sm" style={{ color: theme.textMuted }}>{message}</p>
                <div className="mt-8 space-y-3">
                  <Button variant="primary" theme={theme} onClick={handleRetry} className="w-full py-3">
                    Try Again
                  </Button>
                  <Button variant="secondary" theme={theme} onClick={handleGoToDashboard} className="w-full py-3">
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Help Text */}
        <p className="mt-6 text-center text-xs" style={{ color: theme.textLight }}>
          Having trouble?{" "}
          <a
            href="mailto:support@toronq.ai"
            className="font-semibold hover:underline"
            style={{ color: theme.primary }}
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
